// The Inbox's keyboard and clocks — pure, so the rules are tested, not hoped.
//
// One key per action (Help Scout, Front): J/K move, E handled, # ignore,
// S snooze, Enter open the chat, Z undo, ? all shortcuts, Esc close.
//
// ⚠️ A KEY TYPED INTO A FIELD IS TEXT. « e » inside the ✗ reason box must not
// mark the row handled. Only Escape crosses that line, to leave the field.
//
// Guard: workbench.test.ts

import type { InboxItem } from '@/lib/api';

export type Shortcut = 'next' | 'prev' | 'handle' | 'ignore' | 'snooze' | 'open' | 'undo' | 'help' | 'close';

export function shortcutFor(e: {
  key: string; shiftKey: boolean; metaKey: boolean; ctrlKey: boolean; altKey: boolean;
  targetTag: string; editable: boolean;
}): Shortcut | null {
  if (e.key === 'Escape') return 'close';
  const typing = e.editable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(String(e.targetTag).toUpperCase());
  if (typing || e.metaKey || e.ctrlKey || e.altKey) return null;
  switch (e.key) {
    case 'j': case 'J': case 'ArrowDown': return 'next';
    case 'k': case 'K': case 'ArrowUp': return 'prev';
    case 'e': case 'E': return 'handle';
    case '#': return 'ignore';
    case 's': case 'S': return 'snooze';
    case 'Enter': return 'open';
    case 'z': case 'Z': return 'undo';
    case '?': return 'help';
    default: return null;
  }
}

/** « 12 min », « 1 h 35 », « 19 h », « 3 j » — how long someone has waited. */
export function waitLabel(ageMin: number): string {
  const m = Math.max(0, Math.round(ageMin));
  if (m < 1) return '< 1 min';
  if (m < 60) return `${m} min`;
  if (m < 48 * 60) {
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return h < 3 && rest ? `${h} h ${String(rest).padStart(2, '0')}` : `${h} h`;
  }
  return `${Math.round(m / 1440)} j`;
}

/** « 12 s » / « 5 min » — for the agent's live pill. */
export function agoLabel(iso: string | null | undefined, nowMs: number): string {
  const t = Date.parse(String(iso ?? ''));
  if (!Number.isFinite(t)) return '—';
  const s = Math.max(0, Math.round((nowMs - t) / 1000));
  if (s < 60) return `${s} s`;
  return waitLabel(s / 60);
}

/** Tomorrow 09:00 in Casablanca, as a UTC instant — independent of the viewer's clock. */
/** Midnight today in Casablanca, as an instant. */
function midnightCasablanca(nowMs: number): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Casablanca', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(nowMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const sinceMidnight = ((get('hour') * 60 + get('minute')) * 60 + get('second')) * 1000;
  return Math.floor((nowMs - sinceMidnight) / 1000) * 1000;
}

/**
 * The snooze menu (owner, 2026-10-07): 1 hour · tonight · tomorrow 9:00, plus
 * « pick a time » on screen. « Ce soir 20 h » shows only while 20:00 is still
 * at least an hour away — later, « Dans 1 h » already covers it.
 */
export function snoozeChoices(nowMs: number): Array<{ label: string; until: string }> {
  const midnight = midnightCasablanca(nowMs);
  const tonight = midnight + 20 * 3_600_000;
  return [
    { label: 'Dans 1 h', until: new Date(nowMs + 3_600_000).toISOString() },
    ...(tonight - nowMs >= 3_600_000 ? [{ label: 'Ce soir 20 h', until: new Date(tonight).toISOString() }] : []),
    { label: 'Demain 9 h', until: new Date(midnight + (24 + 9) * 3_600_000).toISOString() },
  ];
}

/**
 * « Choisir l'heure »: a `<input type="datetime-local">` value read as
 * Casablanca time (not the browser's zone), as an ISO instant. null = empty
 * or not in the future.
 */
export function pickedSnoozeIso(local: string, nowMs: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return null;
  const asUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  // The zone's offset at that moment: what Casablanca's clock reads then, minus UTC.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Casablanca', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(asUtc));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const shown = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
  const at = asUtc - (shown - asUtc);
  return at > nowMs ? new Date(at).toISOString() : null;
}

/** Move a selection by one, stopping at the ends; -1 = nothing selectable. */
export function stepIndex(current: number, length: number, dir: 1 | -1): number {
  if (length <= 0) return -1;
  if (current < 0) return 0;
  return Math.min(length - 1, Math.max(0, current + dir));
}

/**
 * How a client is named on screen.
 *
 * The WORKER decides what a name is, and it never sends an `@lid` privacy id as
 * one (clientStage / inbox: « a privacy id is never a name » → empty). Length
 * cannot tell a phone from a privacy id — both run 13–15 digits, and a real
 * Chinese client (+86, 13 digits) was hidden as « Sans nom » on 25 Sept — so
 * digits that reach the screen are a phone. Returns the French key « Sans nom »
 * for no name: pass the result through `t()`.
 */
export function displayName(name: string | null | undefined): string {
  const v = String(name ?? '').trim();
  if (!v) return 'Sans nom';
  const compact = v.replace(/[\s+().-]/g, '');
  if (!/^\d+$/.test(compact)) return v;
  const pretty = (nat: string) => `+212 ${nat.slice(0, 3)} ${nat.slice(3, 5)} ${nat.slice(5, 7)} ${nat.slice(7, 9)}`;
  if (/^212\d{9}$/.test(compact)) return pretty(compact.slice(3));
  if (/^0\d{9}$/.test(compact)) return pretty(compact.slice(1));
  return `+${compact}`;
}

/**
 * One row per client — the manager handles a person, not a list of flags.
 *
 * 25 Sept, first real run: Sehle had 8 rows, Kaoutar 7, Hiba 6 — one handoff,
 * a cancellation, lost photos, alerts… The row shows every reason once, most
 * urgent first; the panel lists each item with its own clock and buttons.
 * Items join on the order OR the chat (a client can have both kinds).
 */
export interface ClientRow {
  key: string;
  tier: InboxItem['tier'];
  overdue: boolean;
  sinceIso: string;
  ageMin: number;
  name: string;
  orderId: string | null;
  orderName: string | null;
  shopifyOrderId: string | null;
  chatId: string | null;
  money: number;
  stageLabel: string | null;
  chips: InboxItem['chips'];
  reasons: string[];
  items: InboxItem[];
}

const TIER_RANK: Record<string, number> = { now: 0, today: 1, old: 2 };
const byUrgency = (a: { tier: string; overdue: boolean; sinceIso: string }, b: { tier: string; overdue: boolean; sinceIso: string }) =>
  (TIER_RANK[a.tier] - TIER_RANK[b.tier]) || (Number(b.overdue) - Number(a.overdue)) || a.sinceIso.localeCompare(b.sinceIso);

export function groupByClient(items: InboxItem[]): ClientRow[] {
  const parent = new Map<string, string>();
  const find = (x: string): string => { while (parent.get(x) !== x) x = parent.get(x) as string; return x; };
  const add = (x: string) => { if (!parent.has(x)) parent.set(x, x); };
  const join = (a: string, b: string) => { add(a); add(b); const ra = find(a); const rb = find(b); if (ra !== rb) parent.set(rb, ra); };
  const nodeOf = (i: InboxItem) => (i.orderId ? `o:${i.orderId}` : i.chatId ? `c:${i.chatId}` : `k:${i.key}`);
  for (const i of items) {
    add(nodeOf(i));
    if (i.orderId && i.chatId) join(`o:${i.orderId}`, `c:${i.chatId}`);
  }
  const groups = new Map<string, InboxItem[]>();
  for (const i of items) {
    const root = find(nodeOf(i));
    groups.set(root, [...(groups.get(root) ?? []), i]);
  }
  const rows: ClientRow[] = [];
  for (const [root, list] of groups) {
    const sorted = [...list].sort(byUrgency);
    const head = sorted[0];
    const inTier = sorted.filter((i) => i.tier === head.tier);
    const withOrder = sorted.find((i) => i.orderId) ?? head;
    rows.push({
      key: `row:${root}`,
      tier: head.tier,
      overdue: inTier.some((i) => i.overdue),
      // The clock of the group the row sits in — an old alert must not make a
      // fresh handoff read « 28 j ».
      sinceIso: [...inTier].sort((a, b) => a.sinceIso.localeCompare(b.sinceIso))[0].sinceIso,
      ageMin: Math.max(...inTier.map((i) => i.ageMin)),
      name: sorted.find((i) => i.name)?.name ?? '',
      orderId: withOrder.orderId, orderName: withOrder.orderName, shopifyOrderId: withOrder.shopifyOrderId,
      chatId: sorted.find((i) => i.chatId)?.chatId ?? null,
      money: Math.max(0, ...sorted.map((i) => i.money)),
      stageLabel: withOrder.stageLabel ?? head.stageLabel,
      chips: sorted.find((i) => i.chips.length)?.chips ?? [],
      reasons: [...new Set(sorted.map((i) => i.reasonLabel))],
      items: sorted,
    });
  }
  return rows.sort(byUrgency);
}

// ─── THE TIMELINE (owner, 2026-09-26: « add the timeline in the dash, the
// default is 7 days ») ────────────────────────────────────────────────────
// The same `{ from, to }` the main dashboard's picker gives
// (components/admin/DateRangePicker.tsx). A row belongs to the period when its
// LAST ACTIVITY does — the server's own rule for the board (routes/pilotage.ts).

/** Is this moment inside the period? « Maximum » (no `from`) keeps everything;
 *  a row with no date is outside any real period, never silently « recent ». */
export function inPeriod(iso: string | null | undefined, range: { from?: string; to?: string }): boolean {
  if (!range.from) return true;
  const t = Date.parse(String(iso ?? ''));
  if (!Number.isFinite(t)) return false;
  if (t < Date.parse(range.from)) return false;
  return !range.to || t <= Date.parse(range.to);
}

/** Split a list by the period. ⚠️ Nothing disappears silently: what the period
 *  hides is COUNTED, so the screen can say so. `keep` pins a row the person is
 *  looking at (an open chat) whatever its date. */
export function splitByPeriod<T>(
  rows: T[],
  when: (row: T) => string | null | undefined,
  range: { from?: string; to?: string },
  keep?: (row: T) => boolean,
): { shown: T[]; outside: number } {
  const shown: T[] = [];
  let outside = 0;
  for (const r of rows) {
    if (inPeriod(when(r), range) || keep?.(r)) shown.push(r);
    else outside += 1;
  }
  return { shown, outside };
}

/** The newest real moment among these — a client is as recent as their latest activity. */
export function latestIso(isos: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestMs = -Infinity;
  for (const iso of isos) {
    const ms = Date.parse(String(iso ?? ''));
    if (Number.isFinite(ms) && ms > bestMs) { bestMs = ms; best = String(iso); }
  }
  return best;
}
